import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import _ from 'lodash';
import {Clerking} from './clerking';
import {hasPermission, Permissions, user} from '../../../../store/reducers/user';
import {useDispatch, useSelector} from 'react-redux';
import {additionalData, setLoading, updatePatientAdditionalData} from '../../../../store/reducers/additionalData';
import keyPerformanceIndicator from '../../../../util/timeMarker';
import Notes from '../../../../util/notes';
import ObservationTable from '../../../../components/observation/observationTable';
import Formatter from '../../../../util/formatter';
import uuidv4 from 'uuid/v4';
import MessageBox from '../../../../util/messageBox';
import useOpenState from '../../../../hooks/useOpenState';
import API from '../../../../app/api';
import {setSnackbar} from '../../../../store/reducers/globals';
import {addNoteForPatient} from '../../../../store/reducers/notes';
import {setWorkflow} from '../../../../store/reducers/workflow';
import {patientBanner} from '../../../../controls/agyle/tasks/components/patientBanner';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import {isCauseForConcernFormComplete, PATIENT_AGE_TYPE} from '../../../../util/patientContextUtil';
import {getSafeguardingValues} from '../formsUtil';
import {getConfig} from '../../../../util/configUtil';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import clsx from 'clsx';
import {WORKFLOW_OPTIONS} from '../../../../util/workflowUtil';
import {openPatientForm} from '../../../../store/reducers/patientContext';
import {AgyleAssessmentIcon} from '../../../../components/icons/agyle';
import ReferralReviewButton from '../referralReview/referralReviewButton';
import {getPatientFlagValue} from '../../mainScreen/util/patientFlagsUtil';

const ClerkingForm = (props) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const patientAddData = useSelector(additionalData);
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const lastNutritionValue = _.last(_.filter(patientAddData?.other?.nutritionScreen, el => !el.canceled && !_.isEmpty(el.weight)));
    const [isRemoveTimestampOpen, openRemoveTimestamp, closeRemoveTimestamp] = useOpenState(false, true);

    const [clerkingObject, setClerkingObject] = useState(null);

    // Other forms states
    const [triageObject, setTriageObject] = React.useState(null);
    const [diagnosis, setDiagnosis] = React.useState(null);

    const [isError, setIsError] = React.useState(false);
    const [errorPicker, setErrorPicker] = React.useState(null);

    const canEditClerking = dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_CLERKING));
    const canEditTriage = dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_TRIAGE));

    const [formIsModified, setFormIsModified] = React.useState(props.isActionMode);

    const [fullFormMode, setFullFormMode] = React.useState(!props.isActionMode);

    const [expanded, setExpanded] = React.useState({
        rockwood: false,
        injury: false,
        delirium: false,
        household: false,
        diagnosis: false,
        peadSafeguarding: false,
    });

    // Configs
    const isAllergiesMandatory = getConfig('allergiesMandatory', false);
    const isCauseForConcernMandatory = getConfig('causeForConcernMandatory', false);
    const referralReviewForm = getConfig('referralReviewForm', false);

    // Others
    const hasSRFlag = getPatientFlagValue(props.patient, 'S&R');
    const [isSmall, setIsSmall] = useState(false);

    useEffect(() => {
        setIsSmall(props._containerWidth < 730)
    }, [props._containerWidth]);

    useEffect(() => {
        if (props.agyleDialogInsertButtons) props.agyleDialogInsertButtons(buttons());

        if (props.onCustomButtons) props.onCustomButtons(buttons());

        if (props.onSubmitFunction) {
            props.onSubmitFunction(sendChange);
        }
    }, [
        clerkingObject,
        fullFormMode,
        errorPicker,
        props.patient,
        props.patientAgeType,
        diagnosis,
        triageObject,
        lastNutritionValue,
        patientAddData,
        isSmall,
    ]);

    useEffect(() => {
        if (props.open) {
            dispatch(setLoading(false));
        } else {
            setClerkingObject(null);
            setFormIsModified(props.isActionMode);
        }
    }, [props.open]);

    useEffect(() => {
        if (props.onDisableSubmit) {
            props.onDisableSubmit(!formIsModified || !canEditClerking || props.disableSave || (isAllergiesMandatory ? (_.isNil(clerkingObject?.allergies) && !props?.isActionMode) : false));
        }
    }, [formIsModified, canEditClerking, clerkingObject, props?.isActionMode]);

    const getClerkingNote = () => {
        return Notes.getClerkingNote(clerkingObject, props.patientAgeType, false, props.patient);
    };

    const getClerkingActionNote = () => {
        return Notes.getActionClerkingNote(clerkingObject);
    };

    const setMainDiagnosis = () => {
        let value = clerkingObject.diagnosis ? clerkingObject.diagnosis : [];
        let diagMain = diagnosis.primary ? diagnosis.primary : [];

        let uniq = [];
        [...diagMain, ...value].forEach((item) => {
            if (!uniq.some((uniqItem) => uniqItem.value?.__code === item.value?.__code))
                uniq.push(item);
        });

        return ({
            ...diagnosis,
            primary: uniq,
        });
    };

    const setMainTriage = () => {
        let clerkingInvestigations = clerkingObject.investigations ? clerkingObject.investigations : [];
        let triageInvestigations = triageObject?.investigationsTriage ? triageObject.investigationsTriage : [];
        let mainInvestigations = triageObject?.investigations ? triageObject.investigations : [];

        let clerkingTreatment = clerkingObject.treatmentAndManagementPlan ? clerkingObject.treatmentAndManagementPlan : [];
        let triageTreatment = triageObject?.treatmentAndManagementPlanTriage ? triageObject.treatmentAndManagementPlanTriage : [];
        let mainTreatment = triageObject?.treatmentAndManagementPlan ? triageObject.treatmentAndManagementPlan : [];

        let uniq = [];

        [...mainInvestigations, ...clerkingInvestigations, ...triageInvestigations].forEach((item) => {
            if (!uniq.some((uniqItem) => uniqItem.value?.__code === item.value?.__code))
                uniq.push(item);
        });

        let uniqTreatment = [];

        [...mainTreatment, ...clerkingTreatment, ...triageTreatment].forEach((item) => {
            if (!uniqTreatment.some((uniqItem) => uniqItem.value?.__code === item.value?.__code))
                uniqTreatment.push(item);
        });

        return ({
            ...triageObject,
            investigations: uniq,
            treatmentAndManagementPlan: uniqTreatment,
        });
    };

    const getSafeguardingValuesClerking = () => {
        let sAll = getSafeguardingValues(props.patient);

        let sClerking = clerkingObject?.safeguarding || [];
        let sPaedsSafeguarding = [];

        return _.uniqWith([...sClerking, ...sAll, ...sPaedsSafeguarding], _.isEqual);
    };

    function calculatePtWeight(weight, units) {
        if (units !== 'kg') {
            return units === 'st' ? _.toString(Number(weight) * 6.35) : _.toString(Number(weight) * 0.45);
        }
        return weight;
    }

    const valid = () => {
        const isValid = !_.isNil(clerkingObject.clinician) && clerkingObject.when && moment(clerkingObject.when).isValid()
            && (fullFormMode ? ((!_.isNil(clerkingObject?.allergies) && isAllergiesMandatory) || !isAllergiesMandatory) : true) && !errorPicker;

        if (!isValid) {
            dispatch(setSnackbar({type: 'warning', message: t('Please check if the data is correct.')}));
        }
        setIsError(!isValid);
        return isValid;
    };

    const sendChange = () => {
        if (valid()) {
            let clone = _.cloneDeep(clerkingObject);
            const timestamp = moment().toISOString();

            if (_.isNil(clone?.firstWho)) {
                clone = {
                    ...clone,
                    firstWho: clone.who,
                    firstWhoUserName: clone.whoUserName,
                    firstWhen: clone.when,
                };
            }

            if (!fullFormMode) {
                clone.status = 'timestamp';

                let modifies = [
                    {
                        name: 'clerking',
                        value: clone,
                        noteToAdd: getClerkingActionNote(),
                    },
                ];

                if (!props.patient?.firstClinician?.who) {
                    modifies.push({
                        name: 'firstClinician',
                        value: {
                            who: clerkingObject?.clinician,
                            when: clerkingObject?.when,
                            timestamp,
                        },
                    });
                }

                if (!props.patient?.currentClinician) {
                    modifies.push({
                        name: 'currentClinician',
                        value: {
                            ...clerkingObject?.clinician,
                            when: clerkingObject?.when,
                            timestamp,
                        },
                    });
                }
                keyPerformanceIndicator(
                    modifies,
                    props.patient,
                    ['firstClinicianSaved'],
                    {
                        when: clone.when,
                        who: clone.who,
                    },
                );
                props.onChange(modifies);
            } else {
                clone.status = 'completed';
                let diag = setMainDiagnosis();

                let triageNew = setMainTriage();

                let modifies = [
                    {
                        name: 'clerking',
                        value: clone,
                        noteToAdd: getClerkingNote(),
                    },
                    {
                        name: 'diagnosis',
                        value: diag,
                    },
                ];

                if (triageNew) {
                    modifies.push({
                        name: 'triage',
                        value: triageNew,
                    });
                }

                modifies.push({
                    name: 'studies.mainScreenSafeguarding',
                    value: getSafeguardingValuesClerking(),
                });

                if (!props.patient?.firstClinician?.who) {
                    modifies.push({
                        name: 'firstClinician',
                        value: {
                            who: clerkingObject?.clinician,
                            when: clerkingObject?.when,
                            timestamp,
                        },
                    });
                }

                if (!props.patient?.currentClinician) {
                    modifies.push({
                        name: 'currentClinician',
                        value: {
                            ...clerkingObject?.clinician,
                            when: clerkingObject?.when,
                            timestamp,
                        },
                    });
                }

                keyPerformanceIndicator(
                    modifies,
                    props.patient,
                    ['firstClinicianSaved'],
                    {
                        when: clone.when,
                        who: clone.who,
                    },
                );

                // Add new record to nutrition screen when clerking pt weight is different from last nutrition record
                if (props.patientAgeType === PATIENT_AGE_TYPE.PAEDS && (!lastNutritionValue
                        || (lastNutritionValue && moment(lastNutritionValue.date).isAfter(props.patient?.admission?.startDate)))
                    && lastNutritionValue?.weight !== clone.patientWeight && !_.isEmpty(clone.patientWeight)) {
                    const listClone = _.cloneDeep(patientAddData?.other?.nutritionScreen || []);
                    const nutritionObj = {
                        id: uuidv4() + '',
                        date: moment().toISOString(),
                        by: tester,
                        login: userObject?.userName,
                        bmi: null,
                        bmiResult: '-',
                        weight: calculatePtWeight(clone.patientWeight, clone.patientWeightUnits),
                        height: null,
                        muac: null,
                        parentForm: 'Clerking',
                    };

                    listClone.push(nutritionObj);

                    const data = {
                        'nutritionScreen': listClone,
                    };
                    dispatch(updatePatientAdditionalData(props.patient.patient.__id, data));
                }

                if (isCauseForConcernMandatory && clone.safeguardingConcerns && !isCauseForConcernFormComplete(props.patient))
                    dispatch(setWorkflow({type: WORKFLOW_OPTIONS.OPEN_CAUSE_FOR_CONCERN_DIALOG}));

                props.onChange(modifies);
            }
        } else {
            return false;
        }
    };

    const handleRemoveTimestamp = () => {
        const data = [{'clerking': null}];
        const isSameFirstClinician = clerkingObject?.clinician?.localCode === props.patient?.firstClinician?.who?.localCode
            && clerkingObject?.when === props.patient?.firstClinician?.when;
        const isSameCurrentClinician = clerkingObject?.clinician?.localCode === props.patient?.currentClinician?.localCode
            && clerkingObject?.when === props.patient?.currentClinician?.when;

        if (isSameFirstClinician) data.push({'firstClinician': null});
        if (isSameCurrentClinician) data.push({'currentClinician': null});

        API.updatePatientInWard(props.patient.__id, data)
            .then((res) => {
                const newVal = [];
                _.forEach(data, d => {
                    Object.keys(d).forEach(key => {
                        newVal.push({
                            name: key,
                            value: d[key],
                        });
                    });
                });
                if (props.modifyPatient) props.modifyPatient(newVal);
                dispatch(addNoteForPatient(props.patient.patient.__id, {
                    note: Notes.getRemoveTimestampNote('clerking', isSameFirstClinician, isSameCurrentClinician),
                    noteType: 'timestamp',
                    patientsInWardsRef: [props.patient.__id],
                    episodeId: props.patient?.admission?.consultantEpisodeId || null,
                }, props.patient.__id));
                dispatch(setSnackbar({type: 'success', message: 'Timestamp successful removed.'}));
            })
            .catch((err) => {
                dispatch(setSnackbar({type: 'error', message: 'Error while removing clerking timestamp.'}));
            })
            .finally(() => {
                closeRemoveTimestamp();
                if (props.onClose) props.onClose();
                if (props._onForceClose) props._onForceClose();
            });
    };

    const accordionChangeHandler = (accordion) => (ev) => {
        setExpanded((prev) =>
            ({
                ...prev,
                [accordion]: !prev[accordion],
            }),
        );
    };

    const handleOpenFullFormTimestamp = () => {
        if (props.handleOpenFullFormTimestamp) props.handleOpenFullFormTimestamp();
    };

    const rockwoodComponent = useMemo(() => {
        return (
            <Grid item xs={12}>
                <AgyleAccordion
                    label={t('Clinical Frailty Score')}
                    expanded={expanded.rockwood}
                    onChange={accordionChangeHandler('rockwood')}
                    formBackground
                >
                    <AgyleButton
                        width="100%"
                        variant="contained"
                        onClick={() => dispatch(openPatientForm('clinicalFrailtyScore'))}
                        startIcon={<AgyleAssessmentIcon/>}
                    >
                        {t('Open Clinical Frailty Score')}
                    </AgyleButton>
                </AgyleAccordion>
            </Grid>
        );
    }, [expanded.rockwood]);

    const InjuryCwiltedComponent = useMemo(() => {
        return (
            <Grid item xs={12}>
                <AgyleAccordion
                    label={props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS ? t('Injury') : t('Cwilted')}
                    expanded={expanded.injury}
                    onChange={accordionChangeHandler('injury')}
                    formBackground
                >
                    <AgyleButton
                        width="100%"
                        variant="contained"
                        onClick={() => dispatch(openPatientForm('injury'))}
                        startIcon={<AgyleAssessmentIcon/>}
                    >
                        {t(`Open ${props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS ? t('Injury') : t('Cwilted')}`)}
                    </AgyleButton>
                </AgyleAccordion>
            </Grid>
        );
    }, [expanded.injury]);

    const DeliriumComponent = useMemo(() => {
        return (
            <Grid item xs={12}>
                <AgyleAccordion
                    label={t('4At Assessment Test For Delirium & Cognitive Impairment')}
                    expanded={expanded.delirium}
                    onChange={accordionChangeHandler('delirium')}
                    formBackground
                >
                    <AgyleButton
                        width="100%"
                        variant="contained"
                        onClick={() => dispatch(openPatientForm('delirium'))}
                        startIcon={<AgyleAssessmentIcon/>}
                    >
                        {t('Open Delirium Screening')}
                    </AgyleButton>
                </AgyleAccordion>
            </Grid>
        );
    }, [expanded.delirium]);

    const PeadSafeguarding = useMemo(() => {
        return (
            <Grid item xs={12}>
                <AgyleAccordion
                    label={t('Paediatric Safeguarding')}
                    expanded={expanded.peadSafeguarding}
                    onChange={accordionChangeHandler('peadSafeguarding')}
                    formBackground
                >
                    <AgyleButton
                        width="100%"
                        variant="contained"
                        onClick={() => dispatch(openPatientForm('paedSafeguarding'))}
                        startIcon={<AgyleAssessmentIcon/>}
                    >
                        {t('Open Paediatric Safeguarding')}
                    </AgyleButton>
                </AgyleAccordion>
            </Grid>
        );
    }, [expanded.peadSafeguarding, clerkingObject]);

    const HouseholdComponent = useMemo(() => {
        return (
            <Grid item xs={12}>
                <AgyleAccordion
                    label={t('Household Information')}
                    expanded={expanded.household}
                    onChange={accordionChangeHandler('household')}
                    formBackground
                >
                    <AgyleButton
                        width="100%"
                        variant="contained"
                        onClick={() => dispatch(openPatientForm('household'))}
                        startIcon={<AgyleAssessmentIcon/>}
                    >
                        {t('Open Household')}
                    </AgyleButton>
                </AgyleAccordion>
            </Grid>
        );
    }, [expanded.household]);

    const buttons = () => {
        const buttons = [];

        if (!fullFormMode) {
            buttons.push(
                <AgyleButton
                    onClick={handleOpenFullFormTimestamp}
                    variant="contained"
                    color="primary"
                >
                    {t('Open Form')}
                </AgyleButton>,
            );
            buttons.push(
                <AgyleButton
                    variant="contained"
                    color="primary"
                    onClick={sendChange}
                >
                    {t('Submit')}
                </AgyleButton>,
            );
        }

        if (referralReviewForm && fullFormMode && hasSRFlag) {
            buttons.push(<ReferralReviewButton small shortText={isSmall}/>);
        }

        if (canEditClerking && !props.disableSave) {
            if (props.patient?.clerking?.status === 'timestamp') {
                buttons.push(
                    <AgyleButton
                        variant="contained"
                        onClick={openRemoveTimestamp}
                    >
                        {t('Remove Timestamp')}
                    </AgyleButton>,
                );
            }
        }
        return buttons;
    };

    if (!props.open) return null;

    return (
        <>
            <Grid container spacing={2} alignItems="center" className={clsx(!fullFormMode && 'action-padding')}>
                {!fullFormMode &&
                    <Grid item xs={12}>
                        {patientBanner(props.patient, false)}
                    </Grid>
                }

                {fullFormMode &&
                    <Grid item xs={12}>
                        <ObservationTable patient={props.patient}/>
                    </Grid>
                }

                <Grid item xs={12}>
                    <Clerking
                        canEditClerking={canEditClerking}
                        canEditTriage={canEditTriage}
                        patientType={props.patientAgeType}
                        saveDiagnosis={setDiagnosis}
                        saveClearking={setClerkingObject}
                        saveTriage={setTriageObject}
                        saveChange={setFormIsModified}
                        pickerError={setErrorPicker}
                        patient={props.patient}
                        fullFormMode={fullFormMode}
                        lastNutrition={lastNutritionValue}
                        isError={isError}
                        openSeniorReview={props.openSeniorReview}
                        pastAttendances={props.pastAttendances}
                        isSmall={isSmall}
                    />
                </Grid>


                {fullFormMode && props.patientAgeType === PATIENT_AGE_TYPE.OVER75 &&
                    <Grid item xs={12}>
                        {rockwoodComponent}
                    </Grid>
                }

                {fullFormMode && props.patientAgeType === PATIENT_AGE_TYPE.OVER75 &&
                    <Grid item xs={12}>
                        {DeliriumComponent}
                    </Grid>
                }

                {fullFormMode && props.patientAgeType === PATIENT_AGE_TYPE.PAEDS &&
                    <Grid item xs={12}>
                        {PeadSafeguarding}
                    </Grid>
                }

                {fullFormMode &&
                    <Grid item xs={12}>
                        {InjuryCwiltedComponent}
                    </Grid>
                }

                {fullFormMode && props.patientAgeType === PATIENT_AGE_TYPE.PAEDS &&
                    <Grid item xs={12}>
                        {HouseholdComponent}
                    </Grid>
                }
            </Grid>

            <MessageBox
                title={t('Confirmation')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={handleRemoveTimestamp}
                onClose={closeRemoveTimestamp}
                message={t('Are you sure you want to remove clerking timestamp?')}
                open={isRemoveTimestampOpen}
            />
        </>
    );
};

export {ClerkingForm};
